// "Add visual" / "Change visual" — the dashboard designer's picker dialog.
// Lists the project's visuals (V) and reports (R), searchable by name, file or
// type; a pick becomes a new tile (or replaces the selected tile's content).
// "Use custom name" keeps the old free-text reference for an object that does
// not exist yet; "Add empty tile" adds a placeholder tile to fill in later.

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  X, Search, ChartColumn, ChartLine, ChartPie, Table2, Gauge, FileText, Plus,
} from 'lucide-react'
import type { ProjectReportSummary, ProjectVisualSummary } from '@/api/designers'

export type TileContentKind = 'visual' | 'report'

export interface AddVisualPick {
  kind: TileContentKind
  /** visual: the VISUAL_CONFIG_REF filename (`model_<obj>.json`); report: its obj name. */
  ref: string
  /** Suggested tile title (the object's name in words). */
  title: string
}

/** `sales_by_region` → `Sales by region`. */
export function humanizeObjName(obj: string): string {
  const s = obj.replace(/^model_/, '').replace(/\.json$/, '').replace(/[_-]+/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : obj
}

export function visualTypeIcon(visualType: string | undefined, size = 18) {
  const t = String(visualType || '').toLowerCase()
  if (/grid|table|pivot|list/.test(t)) return <Table2 size={size} />
  if (/pie|donut|doughnut|sunburst|treemap/.test(t)) return <ChartPie size={size} />
  if (/line|area|trend|spark/.test(t)) return <ChartLine size={size} />
  if (/kpi|card|gauge|indicator|metric/.test(t)) return <Gauge size={size} />
  return <ChartColumn size={size} />
}

export default function AddVisualModal({
  mode, initialKind, projectVisuals, projectReports, usedVisualRefs, usedReports,
  onPick, onAddEmpty, onClose,
}: {
  mode: 'add' | 'replace'
  initialKind: TileContentKind
  projectVisuals: ProjectVisualSummary[]
  projectReports: ProjectReportSummary[]
  /** VISUAL_CONFIG_REF filenames already on the dashboard. */
  usedVisualRefs: ReadonlySet<string>
  usedReports: ReadonlySet<string>
  onPick: (pick: AddVisualPick) => void
  onAddEmpty: () => void
  onClose: () => void
}) {
  const [kind, setKind] = useState<TileContentKind>(initialKind)
  const [q, setQ] = useState('')
  const [sel, setSel] = useState<string>('')
  const [custom, setCustom] = useState(false)
  const [customName, setCustomName] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => { searchRef.current?.focus() }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const rows = kind === 'visual'
      ? projectVisuals.map((v) => ({
          ref: v.filename,
          name: humanizeObjName(v.obj_name),
          hint: `${v.filename}${v.visual_type ? ` · ${v.visual_type}` : ''}${v.schema_name ? ` · ${v.schema_name}` : ''}`,
          type: v.visual_type,
          used: usedVisualRefs.has(v.filename),
          search: `${v.obj_name} ${v.filename} ${v.visual_type} ${v.schema_name}`.toLowerCase(),
        }))
      : projectReports.map((r) => ({
          ref: r.obj_name,
          name: r.title || humanizeObjName(r.obj_name),
          hint: `${r.obj_name}${r.description ? ` · ${r.description}` : ''}`,
          type: 'report',
          used: usedReports.has(r.obj_name),
          search: `${r.obj_name} ${r.title} ${r.description}`.toLowerCase(),
        }))
    return needle ? rows.filter((r) => r.search.includes(needle) || r.name.toLowerCase().includes(needle)) : rows
  }, [kind, q, projectVisuals, projectReports, usedVisualRefs, usedReports])

  const confirm = (ref?: string) => {
    if (custom) {
      const raw = customName.trim()
      if (!raw) return
      const r = kind === 'visual' && !raw.endsWith('.json') ? `model_${raw.replace(/^model_/, '')}.json` : raw
      onPick({ kind, ref: r, title: humanizeObjName(raw) })
      return
    }
    const pick = ref ?? sel
    const row = items.find((i) => i.ref === pick)
    if (!row) return
    onPick({ kind, ref: row.ref, title: row.name })
  }

  const title = mode === 'add' ? 'Add a visual' : 'Change what this tile shows'
  const sub = mode === 'add'
    ? 'Pick a chart, table or report from this project. It is added below the last tile.'
    : 'Pick another visual or report. Bindings that no longer apply are cleared.'

  return (
    <div className="dd-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="dd-modal" role="dialog" aria-modal="true" aria-labelledby="dd-add-title">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 20px 6px' }}>
          <div style={{ flex: '1 1 auto' }}>
            <h2 id="dd-add-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h2>
            <p className="dd-hint" style={{ marginTop: 3 }}>{sub}</p>
          </div>
          <button className="vdo-ib" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={{ padding: '10px 20px 12px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="dd-seg" role="group" aria-label="Content" style={{ flex: '0 0 auto' }}>
            <button aria-pressed={kind === 'visual'} onClick={() => { setKind('visual'); setSel('') }} style={{ padding: '0 14px', flex: '0 0 auto' }}>
              Visuals · {projectVisuals.length}
            </button>
            <button aria-pressed={kind === 'report'} onClick={() => { setKind('report'); setSel('') }} style={{ padding: '0 14px', flex: '0 0 auto' }}>
              Reports · {projectReports.length}
            </button>
          </div>
          {!custom && (
            <div className="dd-msearch">
              <Search size={16} style={{ color: 'var(--color-text-faint)', flexShrink: 0 }} />
              <input
                ref={searchRef}
                aria-label="Search"
                placeholder="Search by name, file or type"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && items.length === 1) confirm(items[0].ref) }}
              />
            </div>
          )}
        </div>
        <div style={{ padding: '0 20px 12px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 200 }}>
          {custom ? (
            <div>
              <label className="dd-lab" htmlFor="dd-custom-ref">
                {kind === 'visual' ? 'Visual object or file name' : 'Report object name'}
              </label>
              <input
                id="dd-custom-ref"
                className="dd-inp mono"
                autoFocus
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') confirm() }}
                placeholder={kind === 'visual' ? 'e.g. sales_by_region' : 'e.g. invoice_register'}
              />
              <p className="dd-hint">
                For an object that does not exist yet. The tile shows a placeholder until it does.
              </p>
            </div>
          ) : (
            <>
              {items.map((it) => (
                <button
                  key={it.ref}
                  className="dd-mrow"
                  role="radio"
                  aria-checked={sel === it.ref}
                  onClick={() => setSel(it.ref)}
                  onDoubleClick={() => confirm(it.ref)}
                >
                  <span className="dd-rad" />
                  <span className="dd-vico">{kind === 'report' ? <FileText size={18} /> : visualTypeIcon(it.type)}</span>
                  <span className="t"><b>{it.name}</b><span className="dd-mono">{it.hint}</span></span>
                  {it.used && <span className="dd-used">On this dashboard</span>}
                </button>
              ))}
              {items.length === 0 && (
                <p className="dd-hint" style={{ textAlign: 'center', padding: '30px 0' }}>
                  {q.trim()
                    ? `Nothing matches “${q.trim()}”.`
                    : kind === 'visual'
                      ? 'No visuals in this project yet. Build one in the Visual designer first.'
                      : 'No reports in this project yet. Build one in the Report designer first.'}
                </p>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <button className="dd-tb link" onClick={() => setCustom((c) => !c)}>
            {custom ? 'Pick from the list' : 'Use custom name'}
          </button>
          <span style={{ fontSize: 13, color: 'var(--color-text-faint)', flex: '1 1 200px' }}>
            Need a new one? Build it in the {kind === 'visual' ? 'Visual' : 'Report'} designer first.
          </span>
          {mode === 'add' && (
            <button className="dd-tb out" onClick={onAddEmpty}>Add empty tile</button>
          )}
          <button
            className="dd-addbtn"
            disabled={custom ? !customName.trim() : !sel}
            onClick={() => confirm()}
          >
            {mode === 'add' ? <><Plus size={16} /> Add to dashboard</> : 'Use this'}
          </button>
        </div>
      </div>
    </div>
  )
}
